import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/personnels/PersonnelsAction";
import * as uiHelpers from "../PersonnelsUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { usePersonnelsUIContext } from "../PersonnelsUIContext";

export function PersonnelsTable({
  renewContracts,
  deleteContracts,
  deletePersonnels
}) {
  // Personnels UI Context
  const personnelsUIContext = usePersonnelsUIContext();
  const personnelsUIProps = useMemo(() => {
    return {
      ids: personnelsUIContext.ids,
      setIds: personnelsUIContext.setIds,
      queryParams: personnelsUIContext.queryParams,
      setQueryParams: personnelsUIContext.setQueryParams,
      openEditPersonnelPage: personnelsUIContext.openEditPersonnelPage
    };
  }, [personnelsUIContext]);

  // Getting curret state of Personnels list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.personnels }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isAdditionAuthorized, isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Personnels Redux state
  const dispatch = useDispatch();

  useEffect(() => {    
    personnelsUIProps.setIds([]);
    dispatch(actions.getPersonnels(personnelsUIProps.queryParams));
    return () => dispatch(actions.cleanPersonnelsState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personnelsUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "photoStr",
      text: "Fotoğraf",
      formatter: columnFormatters.PhotoColumnFormatter,
      style: {
        minWidth: "100px"
      }
    },
    {
      dataField: "tcKimlikNo",
      text: "TC Kimlik No",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "name",
      text: "Adı",
      sort: true,
      sortCaret: sortCaret,
    },    
    {
      dataField: "surname",
      text: "Soyadı",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "personnelType",
      text: "Personel Türü",
      sort: true,
      sortCaret: sortCaret,
    }
  ];

  const actionColumn = {
    dataField: "action",
    text: "İşlemler",
    formatter: columnFormatters.ActionsColumnFormatter,
    formatExtraData: {
      term: personnelsUIProps.queryParams.term,
      isAdditionAuthorized,
      isEditionAuthorized,
      isDeletionAuthorized,
      renewContracts,
      deleteContracts,
      openEditPersonnelPage: personnelsUIProps.openEditPersonnelPage,
      deletePersonnels
    },
    classes: "text-right pr-0",
    headerClasses: "text-right pr-16",
    style: {
      width: "180px",
    }
  }

  if (isAdditionAuthorized || isEditionAuthorized || isDeletionAuthorized) columns.push(actionColumn);

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: personnelsUIProps.queryParams.pageSize,
    page: personnelsUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ?? [] }
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                personnelsUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: personnelsUIProps.ids,
                setIds: personnelsUIProps.setIds,
                hidden: !isAdditionAuthorized && !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}